import React, { useState } from 'react';
import { Card, PrimaryButton, IOTTableWrapper, useSnackbar, DeleteDialog } from '@Iot-Bee/standard-web-library';

import UserDialog from './components/UserDialog';
import { noCacheFetcher } from '../../utils/fetch';
import { useCustomSWR } from '../../hooks/customSWR';
import { EditWebUser, NewWebuser, WebUser } from './interfaces';
import { columns } from './util';

export function UsersPage() {
  const {
    data: webUsers,
    isLoading,
    mutate: refreshWebUsers,
  } = useCustomSWR<WebUser[]>(`${process.env.REACT_APP_API_URL}getwebusers`);
  const [editUser, setEditUser] = useState<WebUser | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; user: undefined | WebUser }>({
    open: false,
    user: undefined,
  });

  const {
    actions: { openSnackbar },
  } = useSnackbar();

  const handleEditUser = (user: EditWebUser) => {
    const currentEncryptedPassword = user.currentPassword;
    const encryptedNewPassword = user.newPassword;

    const body = {
      ...user,
      currentPassword: currentEncryptedPassword,
      newPassword: user.newPassword.length > 0 ? encryptedNewPassword : undefined,
    };

    noCacheFetcher(process.env.REACT_APP_API_URL + `updatewebuser`, {
      method: 'PUT',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(body),
    })
      .then(() => {
        refreshWebUsers();
        openSnackbar('success', 'User updated successfully');
      })
      .catch((err) => {
        console.error('err', err);
        openSnackbar('error', `Error updating user, ${err}`);
      });
  };

  const handleAddUser = (userData: NewWebuser) => {
    const { name, email, role, currentPassword } = userData; // currentPassword is the password in the UserDialog
    const encryptedPassword = currentPassword;
    noCacheFetcher(process.env.REACT_APP_API_URL + `addwebuser`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ name: name, email: email, role: role, password: encryptedPassword }),
    })
      .then(() => {
        refreshWebUsers();
        openSnackbar('success', 'User added successfully');
      })
      .catch((err) => {
        console.error('err', err);
        openSnackbar('error', 'Error adding user');
      });
  };

  const handleDeleteUser = (userId: number) => {
    noCacheFetcher(process.env.REACT_APP_API_URL + `deletewebuser`, {
      method: 'DELETE',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ id: userId }),
    })
      .then(() => {
        openSnackbar('success', 'User deleted successfully');
        refreshWebUsers();
      })
      .catch(() => {
        openSnackbar('error', 'Error deleting user');
      });
  };

  return (
    <Card
      title={'Users'}
      headerActions={
        <PrimaryButton
          onClick={() => {
            setEditUser(undefined);
            setDialogOpen(true);
          }}
        >
          Add new user
        </PrimaryButton>
      }
    >
      <IOTTableWrapper
        columns={columns}
        data={webUsers ?? []}
        onEdit={(userToEdit) => {
          setEditUser(userToEdit);
          setDialogOpen(true);
        }}
        onDelete={(user) => setDeleteDialog({ open: true, user: user })}
        isLoading={isLoading}
      />
      <UserDialog
        user={editUser}
        onClose={() => setDialogOpen(false)}
        open={dialogOpen}
        onSave={(userData: EditWebUser | NewWebuser) => {
          if (editUser) {
            handleEditUser(userData as EditWebUser);
          } else {
            handleAddUser(userData as NewWebuser);
          }
          setDialogOpen(false);
        }}
      />
      {deleteDialog.user !== undefined && (
        <DeleteDialog
          title={deleteDialog.user?.name}
          open={deleteDialog.open}
          handleDelete={() => {
            if (deleteDialog.user) {
              handleDeleteUser(deleteDialog.user.id);
              setDeleteDialog({ open: false, user: undefined });
            } else {
              console.error('No user to delete');
            }
          }}
          onClose={() => setDeleteDialog((state) => ({ ...state, open: false }))}
          itemName={deleteDialog.user?.name}
        />
      )}
    </Card>
  );
}

export default UsersPage;
